import * as ReactDOMNamespace from 'react-dom';
import ReactDOM from 'react-dom';

type FiberLike = {
  child?: FiberLike;
  stateNode?: Element | Text | null;
};

function findDOMNode(componentOrElement: unknown): Element | Text | null {
  if (componentOrElement == null) {
    return null;
  }

  if (componentOrElement instanceof Element || componentOrElement instanceof Text) {
    return componentOrElement;
  }

  const refHost = componentOrElement as { current?: unknown };
  if (refHost.current != null) {
    return findDOMNode(refHost.current);
  }

  const instance = componentOrElement as {
    _reactInternals?: FiberLike;
    _reactInternalFiber?: FiberLike;
  };

  let fiber = instance._reactInternals ?? instance._reactInternalFiber;
  while (fiber) {
    const { stateNode } = fiber;
    if (stateNode instanceof Element || stateNode instanceof Text) {
      return stateNode;
    }
    fiber = fiber.child ?? fiber.sibling;
  }

  return null;
}

/**
 * React 19 removed ReactDOM.findDOMNode. Legacy dependencies (e.g. react-transition-group
 * via @sbx/sbx-ui-components Banner dropdown) still call it in standalone and Hive-embedded MFEs.
 */
export function applyReactDomFindDomNodePolyfill(): void {
  if (typeof ReactDOM.findDOMNode === 'function') {
    return;
  }

  ReactDOM.findDOMNode = findDOMNode;
  (ReactDOMNamespace as typeof ReactDOMNamespace & { findDOMNode?: typeof findDOMNode }).findDOMNode =
    findDOMNode;

  const defaultExport = (ReactDOMNamespace as { default?: { findDOMNode?: typeof findDOMNode } }).default;
  if (defaultExport && typeof defaultExport.findDOMNode !== 'function') {
    defaultExport.findDOMNode = findDOMNode;
  }
}

applyReactDomFindDomNodePolyfill();
